import { useEffect, useState } from 'react'
import { createRoute, redirect, useNavigate } from '@tanstack/react-router'
import { rootRoute } from './__root'
import { apiClient, APIError, setAuthTokens, getRefreshToken } from '@/lib/api'
import { ThemePicker } from '@/components/ThemePicker'
import { BusswerksLogo } from '@/components/BusswerksLogo'
import { isNativePlatform } from '@/lib/nativePlatform'
import { ClaimInviteRequestSchema, type AuthErrorCode } from '@busswerks/shared'

const PENDING_TOKEN_KEY = 'ac.auth.pendingToken'
const PENDING_EMAIL_KEY = 'ac.auth.pendingEmail'

interface PendingInfo {
  email: string
  userId: string
  expiresAt: string
}

async function loadPendingInfo(): Promise<PendingInfo | null> {
  const nativeToken = sessionStorage.getItem(PENDING_TOKEN_KEY)
  const path = nativeToken
    ? `/api/v1/auth/pending-info?pendingToken=${encodeURIComponent(nativeToken)}`
    : '/api/v1/auth/pending-info'
  try {
    return await apiClient.get<PendingInfo>(path)
  } catch {
    return null
  }
}

export const inviteGateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite-gate',
  beforeLoad: async () => {
    // If the user already has a session, route them home — the
    // invite gate is only for users who authenticated but haven't
    // redeemed a code yet.
    try {
      await apiClient.get('/api/v1/auth/me')
      throw redirect({ to: '/home' })
    } catch (err) {
      if (err && typeof err === 'object' && 'to' in err) throw err
      // 401 from /me is the happy path here.
    }
  },
  component: InviteGatePage,
})

function InviteGatePage() {
  const navigate = useNavigate()
  const [pending, setPending] = useState<PendingInfo | null | 'loading'>(
    'loading',
  )
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    void loadPendingInfo().then(info => {
      if (info) {
        setPending(info)
        return
      }
      // No valid pending token — on native we have a stashed email
      // even if the server-side token expired, so surface that with a
      // retry path instead of bouncing back to login immediately.
      const fallbackEmail = sessionStorage.getItem(PENDING_EMAIL_KEY)
      if (fallbackEmail) {
        setPending({
          email: fallbackEmail,
          userId: '',
          expiresAt: new Date().toISOString(),
        })
        setError('Your session timed out — please sign in again to continue.')
      } else {
        void navigate({ to: '/login' })
      }
    })
  }, [navigate])

  const submit = async (ev: React.FormEvent<HTMLFormElement>) => {
    ev.preventDefault()
    setError(null)
    const trimmed = code.trim()
    const parsed = ClaimInviteRequestSchema.safeParse({ code: trimmed })
    if (!parsed.success) {
      setError(
        'Invite codes are 4-64 characters and use letters, digits, and dashes only.',
      )
      return
    }

    setSubmitting(true)
    try {
      const pendingToken =
        sessionStorage.getItem(PENDING_TOKEN_KEY) ?? undefined
      const payload: { code: string; pendingToken?: string } = {
        code: trimmed,
      }
      if (pendingToken) payload.pendingToken = pendingToken

      const res = await apiClient.post<
        { redirectTo: string } | { access: string; refresh: string }
      >('/api/v1/auth/claim-invite', payload)

      // Native response carries tokens in the body; web sets cookies
      // server-side and returns a redirect target.
      if ('access' in res && 'refresh' in res) {
        setAuthTokens(res.access, res.refresh)
        sessionStorage.removeItem(PENDING_TOKEN_KEY)
        sessionStorage.removeItem(PENDING_EMAIL_KEY)
        window.location.href = '/home'
        return
      }

      sessionStorage.removeItem(PENDING_TOKEN_KEY)
      sessionStorage.removeItem(PENDING_EMAIL_KEY)
      window.location.href = res.redirectTo || '/home'
    } catch (err) {
      if (err instanceof APIError) {
        setError(describeClaimError(err))
      } else {
        setError(
          err instanceof Error
            ? err.message
            : 'Something went wrong. Try again.',
        )
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (pending === 'loading') {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-gradient-to-br from-surface-base to-surface-sunken">
        <p className="text-content-tertiary text-sm">Loading…</p>
      </div>
    )
  }
  if (!pending) return null

  // Native auth gate: if the server couldn't verify the pending token
  // but we have a stashed email + refresh token locally, offer a
  // re-auth path instead of leaving the user stranded.
  const hasNativeRefresh = isNativePlatform() && !!getRefreshToken()

  return (
    <div className="min-h-[100dvh] pt-safe pb-safe flex items-center justify-center bg-gradient-to-br from-surface-base to-surface-sunken">
      <div className="absolute top-4 right-4 pt-safe">
        <ThemePicker />
      </div>
      <div className="w-full max-w-md">
        <div className="bg-surface-raised rounded-xl shadow-high p-8 border border-brd-subtle">
          <div className="flex justify-center mb-2">
            <BusswerksLogo size="md" className="text-content-primary" />
          </div>
          <h1 className="text-lg font-semibold text-content-primary text-center mb-1">
            One more step
          </h1>
          <p className="text-content-secondary text-center text-sm mb-6">
            Signed in as{' '}
            <span className="font-mono text-content-primary">
              {pending.email}
            </span>
            . Enter your alpha invite code to finish setting up your account.
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label
                htmlFor="invite-code"
                className="block text-xs font-medium text-content-secondary uppercase tracking-wide mb-1"
              >
                Invite code
              </label>
              <input
                id="invite-code"
                autoFocus
                type="text"
                inputMode="text"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                value={code}
                onChange={e => setCode(e.target.value.toUpperCase())}
                placeholder="ALPHA-ABCD"
                className="w-full px-3 py-2 rounded-lg border border-brd-subtle bg-surface-sunken text-content-primary placeholder-content-tertiary font-mono tracking-wider focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-brd-emphasis"
              />
            </div>

            <button
              type="submit"
              disabled={submitting || !code.trim()}
              className="w-full btn-primary btn-lg font-semibold disabled:opacity-60"
            >
              {submitting ? 'Checking…' : 'Redeem invite'}
            </button>
          </form>

          {error && (
            <div
              role="alert"
              className="mt-4 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
            >
              {error}
            </div>
          )}

          <div className="mt-6 text-center text-content-tertiary text-xs space-y-2">
            <p>
              Don&rsquo;t have a code?{' '}
              <a
                href="mailto:hello@busswerks.io?subject=Busswerks%20alpha%20invite"
                className="underline hover:text-content-secondary"
              >
                Request one
              </a>
              .
            </p>
            <p>
              Wrong account?{' '}
              <a
                href="/api/v1/auth/logout"
                className="underline hover:text-content-secondary"
              >
                Sign out
              </a>
              {hasNativeRefresh && ' (or back to sign-in)'}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

function describeClaimError(err: APIError): string {
  const code = err.code as AuthErrorCode | undefined
  switch (code) {
    case 'invalid_invite_code':
      return "That code isn't valid. Double-check and try again."
    case 'invite_code_exhausted':
      return 'That invite has been used up. Ask for a fresh code.'
    case 'pending_expired':
      return 'Your sign-in session expired. Please sign in again.'
    case 'pending_missing':
      return 'Your sign-in session is missing. Please sign in again.'
    case 'rate_limited':
      return 'Too many attempts. Wait a few minutes and try again.'
    case 'origin_blocked':
      return 'Request was blocked. Reload the page and try again.'
    default:
      return err.message || 'Something went wrong. Try again.'
  }
}
